import { type AxiosInstance, type AxiosRequestConfig } from 'axios';

export interface ActivePlanRes {
  code?: string;
  currencies?: Pricing[];
  customFields?: CustomFieldPlanRes[];
  id?: string;
  /** @format int32 */
  intervalLength: number;
  intervalUnit: IntervalUnits;
  name?: string;
  pricingModel: PricingModels;
  rampIntervals?: RampInterval[];
  setupFees?: SetupFee[] | null;
  /** @format int32 */
  trialLength: number;
  trialRequiresBillingInfo: boolean;
  trialUnit: IntervalUnits;
}

export interface ApiChangeSubscriptionReq {
  /** @minLength 1 */
  planCode: string;
}

export interface ApiCustomerPurchaseReq {
  /** @format int64 */
  authorKey?: number | null;
  /** @minLength 1 */
  billingToken: string;
  /**
   * The key of the article/thread they subscribed from
   * This will coincide with the Context
   * @format int64
   */
  contentKey?: number | null;
  context?: PurchaseContext;
  couponCode?: string | null;
  /** @minLength 1 */
  planCode: string;
  /** @format int32 */
  purchasedOnSiteKey?: number | null;
  referral?: string | null;
  /** @format int32 */
  siteKey?: number | null;
  /** @format int32 */
  sportKey?: number | null;
}

export type ApiUpdatePlanReq = object;

/** Specifies the kind of asset and Asset object is. */
export enum AssetClasses {
  Unknown = 'Unknown',
  Image = 'Image',
  Video = 'Video',
}

export interface AssetResponse {
  altText?: string | null;
  /** Specifies the kind of asset and Asset object is. */
  assetType: AssetClasses;
  caption?: string | null;
  category?: Category;
  description?: string | null;
  domain?: string | null;
  domainOverride?: string | null;
  /** @format int32 */
  duration: number;
  fileSystem?: string | null;
  /** @format int32 */
  height?: number | null;
  /** @format int64 */
  key: number;
  mimeType?: string | null;
  path?: string | null;
  source?: string | null;
  sourceOverride?: string | null;
  thumbnail?: string | null;
  title?: string | null;
  type?: string | null;
  /** @format int32 */
  width?: number | null;
}

export enum Category {
  Undetermined = 'Undetermined',
  Highlights = 'Highlights',
  Training = 'Training',
  Experts = 'Experts',
  Conversations = 'Conversations',
  Camp = 'Camp',
}

export interface CustomFieldPlanRes {
  customField?: CustomFieldRes;
  customFieldId?: string;
  planId?: string;
  value?: string;
}

export interface CustomFieldRes {
  /** @format date-time */
  createdAt: string;
  /** @format date-time */
  deletedAt?: string | null;
  displayName?: string;
  id?: string;
  name?: string;
  relatedType: RelatedRecurlyType;
  tooltip?: string;
  /** @format date-time */
  updatedAt: string;
  userAccess: RecurlyUserAccess;
}

export interface DunningCampaign {
  /** @maxLength 255 */
  code?: string;
  /** @format date-time */
  createdAt: string;
  /** Whether or not this is the default campaign for accounts or plans without an assigned dunning campaign. */
  defaultCampaign: boolean;
  /** @format date-time */
  deletedAt?: string | null;
  /** @maxLength 255 */
  description?: string;
  dunningCycles?: DunningCycle[];
  /** @maxLength 255 */
  id?: string;
  /** @maxLength 255 */
  name?: string;
  /** @format date-time */
  updatedAt: string;
}

export interface DunningCycle {
  /** Whether the dunning settings will be applied to manual trials. Only applies to trial cycles. */
  appliesToManualTrial: boolean;
  /** @format date-time */
  createdAt: string;
  /** Whether the subscription(s) should be cancelled at the end of the dunning cycle. */
  expireSubscription: boolean;
  /** Whether the invoice should be failed at the end of the dunning cycle. */
  failInvoice: boolean;
  /**
   * The number of days after a transaction failure before the first dunning email is sent.
   * @format int32
   */
  firstCommunicationInterval?: number | null;
  intervals?: DunningInterval[];
  /**
   * Whether or not to send an extra email immediately to customers whose initial payment attempt fails with either a
   * hard decline or invalid billing info.
   */
  sendImmediatelyOnHardDecline: boolean;
  /**
   * The number of days between the first dunning email being sent and the end of the dunning cycle.
   * @format int32
   */
  totalDunningDays?: number | null;
  /**
   * The number of days between a transaction failure and the end of the dunning cycle.
   * @format int32
   */
  totalRecyclingDays?: number | null;
  type: DunningCycleType;
  /** @format date-time */
  updatedAt: string;
  /** @format int32 */
  version?: number | null;
}

export enum DunningCycleType {
  Unknown = 'Unknown',
  Automatic = 'Automatic',
  Manual = 'Manual',
  Trial = 'Trial',
}

export interface DunningInterval {
  /**
   * Number of days before sending the next email.
   * @format int32
   */
  days?: number | null;
  emailTemplate?: string;
}

export interface EnsureAccountsExistReq {
  /** @format int32 */
  limit: number;
  /** @format int64 */
  startAfterUserKey: number;
}

export interface EnsureAccountsExistRes {
  /** @format int32 */
  count: number;
  /** @format int64 */
  startAfterUserKey: number;
}

export enum IntervalUnits {
  Unknown = 'Unknown',
  Days = 'Days',
  Months = 'Months',
}

export interface InvoiceSettingsRes {
  accountId?: string;
  billingInfoId?: string;
  /** @format date-time */
  createdAt?: string | null;
  /** @format date-time */
  dueAt?: string | null;
  id?: string;
  number?: string;
  /** @format double */
  paid?: number | null;
  previousInvoiceId?: string;
  state?: string | null;
  subscriptionIds?: string[];
  /** @format double */
  total?: number | null;
  uuid?: string;
}

export interface InvoiceSettingsResPagedData {
  list?: InvoiceSettingsRes[];
  pagination?: Pagination;
}

export interface OfferSetPlansReq {
  annualId?: string | null;
  annualPremiumId?: string | null;
  introId?: string | null;
  monthlyId?: string | null;
  monthlyPremiumId?: string | null;
  winbackId?: string | null;
}

export interface OfferSetPlansRes {
  annualId?: string | null;
  annualPremiumId?: string | null;
  introId?: string | null;
  monthlyId?: string | null;
  monthlyPremiumId?: string | null;
  winbackId?: string | null;
}

export interface OfferSetRes {
  default: boolean;
  /** @format date-time */
  endAt?: string | null;
  /** @format int32 */
  key: number;
  name: string;
  offerSetPlans: OfferSetPlansRes;
  offerSetTemplates: OfferSetTemplatesRes;
  /** @format int32 */
  siteCount: number;
  /** @format date-time */
  startAt?: string | null;
  status: OfferSetStatus;
}

export interface OfferSetResPagedData {
  list?: OfferSetRes[];
  pagination?: Pagination;
}

export interface OfferSetSiteReq {
  /** @format int32 */
  key?: number | null;
  /** @format int32 */
  priority: number;
  /** @format int32 */
  siteKey: number;
}

export interface OfferSetSiteRes {
  /** @format int32 */
  key: number;
  /** @format int32 */
  offerSetKey: number;
  /** @format int32 */
  priority: number;
  /** @format int32 */
  siteKey: number;
  siteName: string;
}

export interface OfferSetSiteResPagedData {
  list?: OfferSetSiteRes[];
  pagination?: Pagination;
}

export enum OfferSetStatus {
  Unknown = 'Unknown',
  Active = 'Active',
  Inactive = 'Inactive',
}

export interface OfferSetTemplateReq {
  planData1?: OfferTemplatePlanData;
  planData2?: OfferTemplatePlanData;
  planData3?: OfferTemplatePlanData;
  planKey1?: string | null;
  planKey2?: string | null;
  planKey3?: string | null;
  data?: OfferTemplateData;
  key: string;
}

export interface OfferSetTemplateRes {
  planData1?: OfferTemplatePlanData;
  planData2?: OfferTemplatePlanData;
  planData3?: OfferTemplatePlanData;
  planKey1?: string | null;
  planKey2?: string | null;
  planKey3?: string | null;
  data?: OfferTemplateData;
  key: string;
}

export interface OfferSetTemplatesReq {
  article?: OfferSetTemplateReq;
  forums?: OfferSetTemplateReq;
  intro?: OfferSetTemplateReq;
  join?: OfferSetTemplateReq;
  topOfContent?: OfferSetTemplateReq;
  winback?: OfferSetTemplateReq;
}

export interface OfferSetTemplatesRes {
  article?: OfferSetTemplateRes;
  forums?: OfferSetTemplateRes;
  intro?: OfferSetTemplateRes;
  join?: OfferSetTemplateRes;
  topOfContent?: OfferSetTemplateRes;
  winback?: OfferSetTemplateRes;
}

export interface OfferTemplateAdminRes {
  planData1: OfferTemplatePlanData;
  planData2: OfferTemplatePlanData;
  planData3: OfferTemplatePlanData;
  planKey1: string | null;
  planKey2: string | null;
  planKey3: string | null;
  data: OfferTemplateData;
  key: string;
  name: string;
  type: OfferTemplateType;
}

export interface OfferTemplateAdminResPagedData {
  list?: OfferTemplateAdminRes[];
  pagination?: Pagination;
}

export interface OfferTemplateData {
  line1?: string | null;
  line2?: string | null;
  line3?: string | null;
  line4?: string | null;
  line5?: string | null;
  title?: string | null;
}

export interface OfferTemplatePlanData {
  line1?: string | null;
  line2?: string | null;
  line3?: string | null;
  line4?: string | null;
  line5?: string | null;
  buttonText?: string | null;
  title?: string | null;
}

export enum OfferTemplateType {
  Unknown = 'Unknown',
  Article = 'Article',
  Forum = 'Forum',
  Intro = 'Intro',
  Join = 'Join',
  TopOfContent = 'TopOfContent',
  Winback = 'Winback',
}

export interface Pagination {
  /** @format int32 */
  count: number;
  /** @format int32 */
  currentPage: number;
  /** @format int32 */
  itemsPerPage: number;
  /** @format int32 */
  limit: number;
  /** @format int32 */
  offset: number;
  /** @format int32 */
  pageCount: number;
}

export interface PlanHostedPages {
  /**
   * If `true`, the customer will be sent directly to your `success_url` after a successful signup,
   * bypassing Recurly's hosted confirmation page.
   */
  bypassConfirmation: boolean;
  /** Determines if the quantity field is displayed on the hosted pages for the plan. */
  displayQuantity: boolean;
  /**
   * URL to redirect to after signup on the hosted payment pages.
   * @format uri
   */
  successUrl?: string | null;
}

export interface PlanRampPricing {
  /**
   * 3-letter ISO 4217 currency code.
   * @maxLength 3
   */
  currency?: string;
  /**
   * The price segment ID or code. For ID no prefix is used e.g. `e28zov4fw0v2`. For requests, the code can also
   * be used. Use prefix `code-`, e.g. `code-gold`.
   */
  priceSegmentId?: string | null;
  /** @format double */
  unitAmount?: number | null;
}

export interface PlanRes {
  accountingCode?: string | null;
  allowAnyItems: boolean;
  autoRenew: boolean;
  /** @format int32 */
  avalaraServiceType?: number | null;
  /** @format int32 */
  avalaraTransactionType?: number | null;
  code?: string;
  /** @format date-time */
  createdAt: string;
  currencies?: Pricing[];
  customFields?: CustomFieldPlanRes[];
  /** @format date-time */
  deletedAt?: string | null;
  description?: string | null;
  dunningCampaign?: DunningCampaign;
  dunningCampaignId?: string | null;
  hostedPages?: PlanHostedPages;
  id?: string;
  /** @format int32 */
  intervalLength: number;
  intervalUnit: IntervalUnits;
  liabilityGlAccountId?: string | null;
  name?: string;
  performanceObligationId?: string | null;
  pricingModel: PricingModels;
  rampIntervals?: RampInterval[];
  revenueGlAccountId?: string | null;
  revenueScheduleType: RecurlyRevenueScheduleType;
  setupFeeAccountingCode?: string | null;
  setupFeeLiabilityGlAccountId?: string | null;
  setupFeePerformanceObligationId?: string | null;
  setupFeeRevenueGlAccountId?: string | null;
  setupFeeRevenueScheduleType?: RecurlyRevenueScheduleType;
  setupFees?: SetupFee[] | null;
  state: RecurlyActiveState;
  taxCode?: string | null;
  taxExempt: boolean;
  /** @format int32 */
  totalBillingCycles?: number | null;
  /** @format int32 */
  trialLength: number;
  trialRequiresBillingInfo: boolean;
  trialUnit: IntervalUnits;
  /** @format date-time */
  updatedAt: string;
  vertexTransactionType?: VertexTransactionType;
}

export interface PlanResPagedData {
  list?: PlanRes[];
  pagination?: Pagination;
}

export interface Pricing {
  /**
   * 3-letter ISO 4217 currency code.
   * @maxLength 3
   */
  currency?: string;
  /**
   * The price segment ID or code. For ID no prefix is used e.g. `e28zov4fw0v2`. For requests, the code can also
   * be used. Use prefix `code-`, e.g. `code-gold`.
   */
  priceSegmentId?: string | null;
  /** @format double */
  setupFee?: number | null;
  /** @format double */
  unitAmount?: number | null;
}

export enum PricingModels {
  Unknown = 'Unknown',
  Fixed = 'Fixed',
  Ramp = 'Ramp',
}

export enum PurchaseContext {
  Unknown = 'Unknown',
  TopOfContent = 'TopOfContent',
  Join = 'Join',
  Article = 'Article',
  Forums = 'Forums',
  Winback = 'Winback',
  Intro = 'Intro',
  Settings = 'Settings',
  NIL = 'NIL',
  Rankings = 'Rankings',
  Profile = 'Profile',
  Recruits = 'Recruits',
  Vitals = 'Vitals',
  TransferPortal = 'TransferPortal',
  IndustryComparison = 'IndustryComparison',
  Admin = 'Admin',
  Import = 'Import',
}

export interface PurchaseLineItemRes {
  name?: string;
  /** @format double */
  price?: number | null;
  /** @format double */
  tax?: number | null;
  type?: PurchaseLineItemType;
}

export enum PurchaseLineItemType {
  Unknown = 'Unknown',
  Charge = 'Charge',
  Credit = 'Credit',
}

export interface PurchaseRes {
  invoiceId?: string;
  invoiceNumber?: string;
  lineItems?: PurchaseLineItemRes[];
  /** @format double */
  nextBillingAmount?: number | null;
  /** @format date-time */
  nextBillingDate?: string | null;
  paymentMethodCardType?: string | null;
  /** @format int32 */
  paymentMethodExpiryMonth?: number | null;
  /** @format int32 */
  paymentMethodExpiryYear?: number | null;
  paymentMethodLastFour?: string | null;
  planName?: string;
  /** @format double */
  planPrice?: number | null;
  /** @format double */
  tax?: number | null;
  /** @format double */
  total?: number | null;
}

export interface RampInterval {
  currencies?: PlanRampPricing[];
  /** @format int32 */
  startingBillingCycle?: number | null;
}

export enum RecurlyActiveState {
  Unknown = 'Unknown',
  Active = 'Active',
  Inactive = 'Inactive',
}

export enum RecurlyRevenueScheduleType {
  Unknown = 'Unknown',
  AtRangeEnd = 'AtRangeEnd',
  AtRangeStart = 'AtRangeStart',
  Evenly = 'Evenly',
  Never = 'Never',
}

export enum RecurlySubscriptionStates {
  Unknown = 'Unknown',
  Active = 'Active',
  Canceled = 'Canceled',
  Expired = 'Expired',
  Failed = 'Failed',
  Future = 'Future',
  Paused = 'Paused',
}

export enum RecurlyUserAccess {
  Unknown = 'Unknown',
  ApiOnly = 'ApiOnly',
  ReadOnly = 'ReadOnly',
  SetOnly = 'SetOnly',
  Write = 'Write',
}

export enum RelatedRecurlyType {
  Unknown = 'Unknown',
  Account = 'Account',
  Charge = 'Charge',
  Item = 'Item',
  Plan = 'Plan',
  Subscription = 'Subscription',
}

export interface RenovateOfferSetReq {
  /** @format date-time */
  endAt?: string | null;
  /** @format int32 */
  key?: number | null;
  name: string;
  offerSetPlans: OfferSetPlansReq;
  offerSetTemplates: OfferSetTemplatesReq;
  /** @format date-time */
  startAt?: string | null;
  status: OfferSetStatus;
}

export interface RenovateOfferTemplateReq {
  planData1?: OfferTemplatePlanData;
  planData2?: OfferTemplatePlanData;
  planData3?: OfferTemplatePlanData;
  planKey1?: string | null;
  planKey2?: string | null;
  planKey3?: string | null;
  data?: OfferTemplateData;
  key?: string;
  name?: string;
  type: OfferTemplateType;
}

export interface SetupFee {
  /**
   * 3-letter ISO 4217 currency code.
   * @maxLength 3
   */
  currency?: string;
  /** @format double */
  unitAmount?: number | null;
}

export interface SitesOfferSetOverviewOfferSetRes {
  annualId?: string | null;
  annualPremiumId?: string | null;
  /** @format date-time */
  endAt?: string | null;
  introId?: string | null;
  /** @format int32 */
  key: number;
  monthlyId?: string | null;
  monthlyPremiumId?: string | null;
  name: string;
  /** @format date-time */
  startAt?: string | null;
  winbackId?: string | null;
}

export interface SitesOfferSetOverviewRelatedRes {
  plans?: Record<string, PlanRes>;
}

export interface SitesOfferSetOverviewRes {
  activeOfferSet?: SitesOfferSetOverviewOfferSetRes;
  /** @format int64 */
  siteKey: number;
  siteName?: string | null;
  siteSlug?: string | null;
}

export interface SitesOfferSetOverviewResSitesOfferSetOverviewRelatedResRelatedPagedData {
  list?: SitesOfferSetOverviewRes[];
  pagination?: Pagination;
  relatedModel?: SitesOfferSetOverviewRelatedRes;
}

export interface SubscriptionSettingsRes {
  accountId?: string;
  autoRenew?: boolean | null;
  billingInfoId?: string | null;
  /** @format date-time */
  canceledAt?: string | null;
  /** @format date-time */
  currentPeriodEndsAt?: string | null;
  /** @format date-time */
  currentPeriodStartedAt?: string | null;
  /** @format date-time */
  expiresAt?: string | null;
  id?: string;
  isActive: boolean;
  isPremium: boolean;
  /** @format date-time */
  pausedAt?: string | null;
  planId?: string;
  planName?: string;
  /** @format int32 */
  remainingPauseCycles?: number | null;
  siteAvatar?: AssetResponse;
  /** @format int32 */
  siteKey?: number | null;
  siteName?: string | null;
  sitePrimaryColor?: string | null;
  state?: RecurlySubscriptionStates;
  uuid?: string;
}

export interface SubscriptionSettingsResPagedData {
  list?: SubscriptionSettingsRes[];
  pagination?: Pagination;
}

export interface SyncCountRes {
  /** @format int32 */
  added: number;
  /** @format int32 */
  deleted: number;
  /** @format int32 */
  modified: number;
  /** @format int32 */
  total: number;
  /** @format int32 */
  unchanged: number;
  /** @format int32 */
  updated: number;
}

export interface V1OfferSetsListParams {
  /**
   * @format int32
   * @min 1
   * @max 100
   * @default 25
   */
  limit?: number;
  /**
   * @format int32
   * @default 1
   */
  page?: number;
  /** @format int32 */
  siteKey?: number;
}

export interface V1OfferSetsOverviewListParams {
  /**
   * @format int32
   * @min 1
   * @max 100
   * @default 25
   */
  limit?: number;
  /**
   * @format int32
   * @default 1
   */
  page?: number;
}

export interface V1OfferSetsSitesDetailParams {
  /** @format int32 */
  offerSetKey: number;
  /**
   * @format int32
   * @default 1
   */
  page?: number;
}

export type V1OfferSetsSitesUpdatePayload = OfferSetSiteReq[];

export interface V1OfferTemplatesListParams {
  /**
   * @format int32
   * @min 1
   * @max 100
   * @default 25
   */
  limit?: number;
  /**
   * @format int32
   * @default 1
   */
  page?: number;
  type?: OfferTemplateType[];
}

export interface V1PlansAdminListParams {
  /**
   * Allowed number of items per page.
   * 1, 5, 10, 25, 50 are valid if provided.
   * @format int32
   * @default 25
   */
  limit?: number;
  /**
   * @format int32
   * @min 1
   * @max 2147483647
   * @default 1
   */
  page?: number;
}

export enum VertexTransactionType {
  Unknown = 'Unknown',
  Lease = 'Lease',
  Rental = 'Rental',
  Sale = 'Sale',
}

export type QueryParamsType = Record<string | number, any>;

export type RequestParams = Omit<
  AxiosRequestConfig,
  'data' | 'method' | 'params' | 'url' | 'responseType'
>;

export enum ContentType {
  Json = 'application/json',
  FormData = 'multipart/form-data',
  UrlEncoded = 'application/x-www-form-urlencoded',
  Text = 'text/plain',
}

/**
 * No description
 *
 * @tags Account
 * @name V1AccountsEnsureBatchExistCreate
 * @summary (Roles: developer)
 * @request POST:/subs/v1/accounts/ensure-batch-exist
 * @secure
 */
export const v1AccountsEnsureBatchExistCreate = (
  client: AxiosInstance,
  data: EnsureAccountsExistReq,
  params: RequestParams = {},
) =>
  client
    .request<EnsureAccountsExistRes>({
      url: `/subs/v1/accounts/ensure-batch-exist`,
      method: 'POST',
      data,
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags BusinessEntity
 * @name V1BusinessEntitiesSyncCreate
 * @summary (Policies: service-account:cron:offer-set:modify)
 * @request POST:/subs/v1/business-entities/sync
 * @secure
 */
export const v1BusinessEntitiesSyncCreate = (
  client: AxiosInstance,
  params: RequestParams = {},
) =>
  client
    .request<SyncCountRes>({
      url: `/subs/v1/business-entities/sync`,
      method: 'POST',
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags CustomField
 * @name V1CustomFieldsSyncCreate
 * @summary (Policies: service-account:cron:offer-set:modify)
 * @request POST:/subs/v1/custom-fields/sync
 * @secure
 */
export const v1CustomFieldsSyncCreate = (
  client: AxiosInstance,
  params: RequestParams = {},
) =>
  client
    .request<SyncCountRes>({
      url: `/subs/v1/custom-fields/sync`,
      method: 'POST',
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags DunningCampaign
 * @name V1DunningCampaignsSyncCreate
 * @summary (Policies: service-account:cron:offer-set:modify)
 * @request POST:/subs/v1/dunning-campaigns/sync
 * @secure
 */
export const v1DunningCampaignsSyncCreate = (
  client: AxiosInstance,
  params: RequestParams = {},
) =>
  client
    .request<SyncCountRes>({
      url: `/subs/v1/dunning-campaigns/sync`,
      method: 'POST',
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags ExternalProduct
 * @name V1ExternalProductsSyncCreate
 * @summary (Policies: service-account:cron:offer-set:modify)
 * @request POST:/subs/v1/external-products/sync
 * @secure
 */
export const v1ExternalProductsSyncCreate = (
  client: AxiosInstance,
  params: RequestParams = {},
) =>
  client
    .request<SyncCountRes>({
      url: `/subs/v1/external-products/sync`,
      method: 'POST',
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags Item
 * @name V1ItemsSyncCreate
 * @summary (Policies: service-account:cron:offer-set:modify)
 * @request POST:/subs/v1/items/sync
 * @secure
 */
export const v1ItemsSyncCreate = (
  client: AxiosInstance,
  params: RequestParams = {},
) =>
  client
    .request<SyncCountRes>({
      url: `/subs/v1/items/sync`,
      method: 'POST',
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags OfferSet
 * @name V1OfferSetsDelete
 * @summary (Policies: offer-set:modify)
 * @request DELETE:/subs/v1/offer-sets/{offerSetKey}
 * @secure
 */
export const v1OfferSetsDelete = (
  client: AxiosInstance,
  offerSetKey: number,
  params: RequestParams = {},
) =>
  client
    .request<void>({
      url: `/subs/v1/offer-sets/${offerSetKey}`,
      method: 'DELETE',
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags OfferSet
 * @name V1OfferSetsDetail
 * @summary (Policies: offer-set:read)
 * @request GET:/subs/v1/offer-sets/{offerSetKey}
 * @secure
 */
export const v1OfferSetsDetail = (
  client: AxiosInstance,
  offerSetKey: number,
  params: RequestParams = {},
) =>
  client
    .request<OfferSetRes>({
      url: `/subs/v1/offer-sets/${offerSetKey}`,
      method: 'GET',
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags OfferSet
 * @name V1OfferSetsList
 * @summary (Policies: offer-set:read)
 * @request GET:/subs/v1/offer-sets
 * @secure
 */
export const v1OfferSetsList = (
  client: AxiosInstance,
  query: V1OfferSetsListParams,
  params: RequestParams = {},
) =>
  client
    .request<OfferSetResPagedData>({
      url: `/subs/v1/offer-sets`,
      method: 'GET',
      params: query,
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags OfferSet
 * @name V1OfferSetsOverviewList
 * @summary (Policies: offer-set:read)
 * @request GET:/subs/v1/offer-sets-overview
 * @secure
 */
export const v1OfferSetsOverviewList = (
  client: AxiosInstance,
  query: V1OfferSetsOverviewListParams,
  params: RequestParams = {},
) =>
  client
    .request<SitesOfferSetOverviewResSitesOfferSetOverviewRelatedResRelatedPagedData>(
      {
        url: `/subs/v1/offer-sets-overview`,
        method: 'GET',
        params: query,
        ...params,
      },
    )
    .then((r) => r.data);

/**
 * No description
 *
 * @tags OfferSet
 * @name V1OfferSetsSitesDelete
 * @summary (Policies: offer-set:modify)
 * @request DELETE:/subs/v1/offer-sets/{offerSetKey}/sites/{siteKey}
 * @secure
 */
export const v1OfferSetsSitesDelete = (
  client: AxiosInstance,
  offerSetKey: number,
  siteKey: number,
  params: RequestParams = {},
) =>
  client
    .request<void>({
      url: `/subs/v1/offer-sets/${offerSetKey}/sites/${siteKey}`,
      method: 'DELETE',
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags OfferSet
 * @name V1OfferSetsSitesDetail
 * @summary (Policies: offer-set:read)
 * @request GET:/subs/v1/offer-sets/{offerSetKey}/sites
 * @secure
 */
export const v1OfferSetsSitesDetail = (
  client: AxiosInstance,
  { offerSetKey, ...query }: V1OfferSetsSitesDetailParams,
  params: RequestParams = {},
) =>
  client
    .request<OfferSetSiteResPagedData>({
      url: `/subs/v1/offer-sets/${offerSetKey}/sites`,
      method: 'GET',
      params: query,
      ...params,
    })
    .then((r) => r.data);

/**
 * @description Add/Update sites in an Offer Set
 *
 * @tags OfferSet
 * @name V1OfferSetsSitesUpdate
 * @summary (Policies: offer-set:modify)
 * @request PUT:/subs/v1/offer-sets/{offerSetKey}/sites
 * @secure
 */
export const v1OfferSetsSitesUpdate = (
  client: AxiosInstance,
  offerSetKey: number,
  data: V1OfferSetsSitesUpdatePayload,
  params: RequestParams = {},
) =>
  client
    .request<OfferSetSiteRes[]>({
      url: `/subs/v1/offer-sets/${offerSetKey}/sites`,
      method: 'PUT',
      data,
      ...params,
    })
    .then((r) => r.data);

/**
 * @description Create a new Offer Set or Update an existing Offer Set.
 *
 * @tags OfferSet
 * @name V1OfferSetsUpdate
 * @summary (Policies: offer-set:read)
 * @request PUT:/subs/v1/offer-sets
 * @secure
 */
export const v1OfferSetsUpdate = (
  client: AxiosInstance,
  data: RenovateOfferSetReq,
  params: RequestParams = {},
) =>
  client
    .request<OfferSetRes>({
      url: `/subs/v1/offer-sets`,
      method: 'PUT',
      data,
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags OfferTemplate
 * @name V1OfferTemplatesDelete
 * @summary (Policies: offer-set:modify)
 * @request DELETE:/subs/v1/offer-templates/{key}
 * @secure
 */
export const v1OfferTemplatesDelete = (
  client: AxiosInstance,
  key: string,
  params: RequestParams = {},
) =>
  client
    .request<void>({
      url: `/subs/v1/offer-templates/${key}`,
      method: 'DELETE',
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags OfferTemplate
 * @name V1OfferTemplatesDetail
 * @summary (Policies: offer-set:read)
 * @request GET:/subs/v1/offer-templates/{key}
 * @secure
 */
export const v1OfferTemplatesDetail = (
  client: AxiosInstance,
  key: string,
  params: RequestParams = {},
) =>
  client
    .request<OfferTemplateAdminRes>({
      url: `/subs/v1/offer-templates/${key}`,
      method: 'GET',
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags OfferTemplate
 * @name V1OfferTemplatesList
 * @summary (Policies: offer-set:read)
 * @request GET:/subs/v1/offer-templates
 * @secure
 */
export const v1OfferTemplatesList = (
  client: AxiosInstance,
  query: V1OfferTemplatesListParams,
  params: RequestParams = {},
) =>
  client
    .request<OfferTemplateAdminResPagedData>({
      url: `/subs/v1/offer-templates`,
      method: 'GET',
      params: query,
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags OfferTemplate
 * @name V1OfferTemplatesRenovateUpdate
 * @summary (Policies: offer-set:modify)
 * @request PUT:/subs/v1/offer-templates/renovate
 * @secure
 */
export const v1OfferTemplatesRenovateUpdate = (
  client: AxiosInstance,
  data: RenovateOfferTemplateReq,
  params: RequestParams = {},
) =>
  client
    .request<void>({
      url: `/subs/v1/offer-templates/renovate`,
      method: 'PUT',
      data,
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags Plan
 * @name V1PlansAdminList
 * @summary (Roles: cs_admin, developer)
 * @request GET:/subs/v1/plans/admin
 * @secure
 */
export const v1PlansAdminList = (
  client: AxiosInstance,
  query: V1PlansAdminListParams,
  params: RequestParams = {},
) =>
  client
    .request<PlanResPagedData>({
      url: `/subs/v1/plans/admin`,
      method: 'GET',
      params: query,
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags Plan
 * @name V1PlansDetail
 * @request GET:/subs/v1/plans/{planId}
 */
export const v1PlansDetail = (
  client: AxiosInstance,
  planId: string,
  params: RequestParams = {},
) =>
  client
    .request<ActivePlanRes>({
      url: `/subs/v1/plans/${planId}`,
      method: 'GET',
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags Plan
 * @name V1PlansEnsureDefaultCreate
 * @summary (Roles: developer)
 * @request POST:/subs/v1/plans/ensure-default
 * @secure
 */
export const v1PlansEnsureDefaultCreate = (
  client: AxiosInstance,
  params: RequestParams = {},
) =>
  client
    .request<PlanResPagedData>({
      url: `/subs/v1/plans/ensure-default`,
      method: 'POST',
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags Plan
 * @name V1PlansSyncCreate
 * @summary (Policies: service-account:cron:offer-set:modify)
 * @request POST:/subs/v1/plans/sync
 * @secure
 */
export const v1PlansSyncCreate = (
  client: AxiosInstance,
  params: RequestParams = {},
) =>
  client
    .request<SyncCountRes>({
      url: `/subs/v1/plans/sync`,
      method: 'POST',
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags Plan
 * @name V1PlansUpdate
 * @summary (Roles: cs_admin, developer)
 * @request PUT:/subs/v1/plans/{planId}
 * @secure
 */
export const v1PlansUpdate = (
  client: AxiosInstance,
  planId: string,
  data: ApiUpdatePlanReq,
  params: RequestParams = {},
) =>
  client
    .request<PlanRes>({
      url: `/subs/v1/plans/${planId}`,
      method: 'PUT',
      data,
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags Purchase
 * @name V1PurchaseCreate
 * @summary Create purchase as a customer
 * @request POST:/subs/v1/purchase
 * @secure
 */
export const v1PurchaseCreate = (
  client: AxiosInstance,
  data: ApiCustomerPurchaseReq,
  params: RequestParams = {},
) =>
  client
    .request<PurchaseRes>({
      url: `/subs/v1/purchase`,
      method: 'POST',
      data,
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags Subscription
 * @name V1SubscriptionsChangePlanCreate
 * @summary (Roles: registered user)
 * @request POST:/subs/v1/subscriptions/{subscriptionId}/change-plan
 * @secure
 */
export const v1SubscriptionsChangePlanCreate = (
  client: AxiosInstance,
  subscriptionId: string,
  data: ApiChangeSubscriptionReq,
  params: RequestParams = {},
) =>
  client
    .request<SubscriptionSettingsRes>({
      url: `/subs/v1/subscriptions/${subscriptionId}/change-plan`,
      method: 'POST',
      data,
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags Subscription
 * @name V1SubscriptionsToggleAutoRenewDetail
 * @summary (Roles: registered user)
 * @request GET:/subs/v1/subscriptions/{subscriptionId}/toggle-auto-renew
 * @secure
 */
export const v1SubscriptionsToggleAutoRenewDetail = (
  client: AxiosInstance,
  subscriptionId: string,
  params: RequestParams = {},
) =>
  client
    .request<SubscriptionSettingsRes>({
      url: `/subs/v1/subscriptions/${subscriptionId}/toggle-auto-renew`,
      method: 'GET',
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags ExternalSubscriptions
 * @name V1UsersExternalSubscriptionsSyncCreate
 * @summary (Roles: cs_admin)
 * @request POST:/subs/v1/users/{userKey}/external-subscriptions/sync
 * @secure
 */
export const v1UsersExternalSubscriptionsSyncCreate = (
  client: AxiosInstance,
  userKey: number,
  params: RequestParams = {},
) =>
  client
    .request<SyncCountRes>({
      url: `/subs/v1/users/${userKey}/external-subscriptions/sync`,
      method: 'POST',
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags Invoice
 * @name V1UsersInvoicesSettingsDetail
 * @summary (Roles: registered user)
 * @request GET:/subs/v1/users/{userKey}/invoices/settings
 * @secure
 */
export const v1UsersInvoicesSettingsDetail = (
  client: AxiosInstance,
  userKey: number,
  params: RequestParams = {},
) =>
  client
    .request<InvoiceSettingsResPagedData>({
      url: `/subs/v1/users/${userKey}/invoices/settings`,
      method: 'GET',
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags Subscription
 * @name V1UsersSubscriptionsSettingsDetail
 * @summary (Roles: registered user)
 * @request GET:/subs/v1/users/{userKey}/subscriptions/settings
 * @secure
 */
export const v1UsersSubscriptionsSettingsDetail = (
  client: AxiosInstance,
  userKey: number,
  params: RequestParams = {},
) =>
  client
    .request<SubscriptionSettingsResPagedData>({
      url: `/subs/v1/users/${userKey}/subscriptions/settings`,
      method: 'GET',
      ...params,
    })
    .then((r) => r.data);

/**
 * No description
 *
 * @tags Subscription
 * @name V1UsersSubscriptionsSyncCreate
 * @summary (Roles: cs_admin)
 * @request POST:/subs/v1/users/{userKey}/subscriptions/sync
 * @secure
 */
export const v1UsersSubscriptionsSyncCreate = (
  client: AxiosInstance,
  userKey: number,
  params: RequestParams = {},
) =>
  client
    .request<SyncCountRes>({
      url: `/subs/v1/users/${userKey}/subscriptions/sync`,
      method: 'POST',
      ...params,
    })
    .then((r) => r.data);
